import { useState } from 'react';
import { tokens } from './tokens';

type FieldType = 'text' | 'email' | 'password' | 'tel';

type Props = Readonly<{
  name: string;
  label: string;
  type?: FieldType;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  maxLength?: number;
  error?: string;
}>;

function resolveBorderColor(error: string | undefined, focused: boolean): string {
  // An edge a person must see to find the field: 3:1 against the surface (WCAG 1.4.11).
  if (error) return tokens.errorText;
  if (focused) return tokens.goldOnLight;
  return tokens.controlBorder;
}

/**
 * Opinionated single-line input. Fixed sizing, fixed border radius, fixed
 * focus animation, fixed label typography. Only the label/value/state are
 * configurable — visual style is locked to enforce design consistency.
 */
export function FormField({
  name,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  autoComplete,
  maxLength,
  error,
}: Props): JSX.Element {
  const [focused, setFocused] = useState(false);
  const borderColor = resolveBorderColor(error, focused);

  return (
    <div>
      <label
        htmlFor={name}
        className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
        style={{ color: tokens.textLabel }}
      >
        {label}
        {required && <span style={{ color: tokens.errorText }}> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${name}-error` : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
        style={{
          background: disabled ? tokens.disabledBg : tokens.surfaceMuted,
          border: `1.5px solid ${borderColor}`,
          color: tokens.textPrimary,
        }}
      />
      {error && (
        <p id={`${name}-error`} className="mt-1.5 text-xs" style={{ color: tokens.errorText }}>
          {error}
        </p>
      )}
    </div>
  );
}
